import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Bug, CheckCircle2, Trash2, RefreshCw, Filter, RotateCcw } from 'lucide-react';

const roleLabel = (r) => ({
  admin: 'Administrator', fleet_manager: 'Manager Flotă', contabilitate: 'Contabilitate',
  juridic: 'Juridic', dispatcher: 'Dispecer', tesa: 'TESA', driver: 'Șofer',
}[r] || r || '-');

// Afișează pozele atașate unui raport (încărcate cu token, nu direct prin <img src>).
const ReportPhotos = ({ attachments, getAuthHeaders }) => {
  const [urls, setUrls] = useState([]);

  useEffect(() => {
    let revoked = [];
    let cancelled = false;
    (async () => {
      const out = [];
      for (const a of attachments) {
        try {
          const res = await axios.get(`${API}/error-reports/photo/${a.id}`, { ...getAuthHeaders(), responseType: 'blob' });
          const url = URL.createObjectURL(res.data);
          revoked.push(url);
          out.push(url);
        } catch { /* ignoră poza care nu se poate încărca */ }
      }
      if (!cancelled) setUrls(out);
    })();
    return () => { cancelled = true; revoked.forEach(u => { try { URL.revokeObjectURL(u); } catch { /* noop */ } }); };
  }, [attachments, getAuthHeaders]);

  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid="error-report-photos">
      {urls.map((u, i) => (
        <a key={i} href={u} target="_blank" rel="noreferrer" className="block w-20 h-20 rounded-md overflow-hidden border border-slate-200" data-testid={`error-report-photo-${i}`}>
          <img src={u} alt={`poza-${i}`} className="w-full h-full object-cover" />
        </a>
      ))}
    </div>
  );
};

export const ErrorReportsTab = ({ getAuthHeaders }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open'); // open | all
  const [userFilter, setUserFilter] = useState('');
  const [debouncedUser, setDebouncedUser] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedUser(userFilter), 400);
    return () => clearTimeout(t);
  }, [userFilter]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter === 'open') params.append('resolved', 'false');
      if (debouncedUser) params.append('user', debouncedUser);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await axios.get(`${API}/error-reports?${params.toString()}`, getAuthHeaders());
      setReports(res.data || []);
    } catch {
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [filter, debouncedUser, dateFrom, dateTo, getAuthHeaders]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const toggleResolved = async (r) => {
    try {
      await axios.put(`${API}/error-reports/${r.id}/resolve?resolved=${!r.resolved}`, {}, getAuthHeaders());
      toast.success(r.resolved ? 'Marcat ca nerezolvat' : 'Marcat ca rezolvat');
      fetchReports();
    } catch { toast.error('Eroare la actualizare'); }
  };

  const remove = async (r) => {
    if (!window.confirm('Ștergi acest raport?')) return;
    try {
      await axios.delete(`${API}/error-reports/${r.id}`, getAuthHeaders());
      toast.success('Raport șters');
      fetchReports();
    } catch { toast.error('Eroare la ștergere'); }
  };

  const fmt = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleString('ro-RO'); } catch { return iso; }
  };

  return (
    <div className="space-y-4" data-testid="error-reports-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bug className="w-5 h-5 text-red-600" />
            Rapoarte de Erori
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Stare</label>
              <select value={filter} onChange={e => setFilter(e.target.value)}
                className="text-sm border border-slate-300 rounded-md px-3 py-2 bg-white" data-testid="error-reports-filter">
                <option value="open">Nerezolvate</option>
                <option value="all">Toate</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Utilizator</label>
              <Input value={userFilter} onChange={e => setUserFilter(e.target.value)} placeholder="username" className="w-40" data-testid="error-reports-user-filter" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">De la</label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-40" data-testid="error-reports-date-from" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Până la</label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-40" data-testid="error-reports-date-to" />
            </div>
            <Button variant="outline" size="sm" onClick={fetchReports} data-testid="error-reports-refresh">
              <RefreshCw className="w-4 h-4 mr-1.5" />Reîncarcă
            </Button>
          </div>

          {loading ? (
            <p className="text-center text-slate-500 py-8">Se încarcă...</p>
          ) : reports.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-300" />
              <p className="text-sm">Niciun raport{filter === 'open' ? ' nerezolvat' : ''}. Totul pare în regulă.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="error-reports-list">
              {reports.map(r => (
                <div key={r.id} className={`rounded-lg border p-3 ${r.resolved ? 'bg-slate-50 border-slate-200 opacity-70' : 'bg-white border-red-200'}`} data-testid={`error-report-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-800">{r.username || '-'}</span>
                        <Badge variant="secondary" className="text-[10px]">{roleLabel(r.role)}</Badge>
                        {r.resolved && <Badge className="bg-green-600 text-[10px]">Rezolvat</Badge>}
                        <span className="text-xs text-slate-400">{fmt(r.created_at)}</span>
                      </div>
                      {r.message && <p className="text-sm text-slate-700 mt-1.5 whitespace-pre-wrap">{r.message}</p>}
                      {r.app_section && <p className="text-xs text-slate-500 mt-1">Secțiune: {r.app_section}</p>}
                      {Array.isArray(r.recent_errors) && r.recent_errors.length > 0 && (
                        <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
                          {r.recent_errors.map((e, i) => (
                            <div key={i} className="font-mono truncate">
                              {e.status || '—'} · {(e.method || '').toUpperCase()} {e.endpoint || ''} {e.detail ? `· ${e.detail}` : ''}
                            </div>
                          ))}
                        </div>
                      )}
                      {r.user_agent && <p className="text-[10px] text-slate-400 mt-1 truncate">{r.user_agent}</p>}
                      <ReportPhotos attachments={r.attachments} getAuthHeaders={getAuthHeaders} />
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => toggleResolved(r)} title={r.resolved ? 'Marchează ca nerezolvat' : 'Marchează ca rezolvat'} className={r.resolved ? '' : 'text-green-600 hover:bg-green-50 border-green-200'} data-testid={`error-report-resolve-${r.id}`}>
                        {r.resolved ? <RotateCcw className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => remove(r)} title="Șterge raportul" className="text-red-600 hover:bg-red-50 border-red-200" data-testid={`error-report-delete-${r.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ErrorReportsTab;
